import { palette } from '@shipium-corp/shipium-ui-theme';
import type { Address } from '../../types';
import type { SimpleMapMarker } from './SimpleMap';

const MAX_POSSIBLE_LAT_OR_LNG_VALUE = 180;
const MIN_POSSIBLE_LAT_OR_LNG_VALUE = -180;

// TODO: move these palettes to @shipium-corp/shipium-ui-theme so all apps share the same chart palette tokens.
const palettes = {
  // Option 1: brand-token palette, hues interleaved (no two adjacent slots share a hue).
  // Pros: fully on-brand. Cons: only 6 hues, so 7th+ slot reuses a hue with a shade variant.
  brandHueInterleaved: [
    palette.Blue.normal,
    palette.Coral.normal,
    palette.Teal.normal,
    palette.Gold.normal,
    palette.Violet.normal,
    palette.Fuschia.normal,
    palette.Blue.dark,
    palette.Coral.dark,
    palette.Teal.dark,
    palette.Gold.dark,
    palette.Violet.dark,
    palette.Fuschia.dark,
  ],
  // Option 2: Tableau 10 — most-used categorical palette in BI/analytics tools.
  // Pros: well-tested, 10 distinct hues. Cons: vivid; can compete with map tile content.
  tableau10: [
    '#4E79A7', // blue
    '#F28E2B', // orange
    '#E15759', // red
    '#76B7B2', // teal
    '#59A14F', // green
    '#EDC948', // yellow
    '#B07AA1', // purple
    '#FF9DA7', // pink
    '#9C755F', // brown
    '#BAB0AC', // grey
  ],
  // Option 3: D3 schemeCategory10 — D3's classic categorical palette.
  // Pros: ubiquitous in custom visualizations. Cons: slightly older feel than Tableau 10.
  d3Category10: [
    '#1F77B4', // blue
    '#FF7F0E', // orange
    '#2CA02C', // green
    '#D62728', // red
    '#9467BD', // purple
    '#8C564B', // brown
    '#E377C2', // pink
    '#7F7F7F', // grey
    '#BCBD22', // yellow-green
    '#17BECF', // cyan
  ],
  // Option 4: IBM Carbon Categorical — used across IBM enterprise products.
  // Pros: lower saturation reads well over map tiles and busy backgrounds; accessible.
  // Cons: less "fun" than Tableau on a clean white page.
  ibmCarbon: [
    '#6929C4', // purple
    '#1192E8', // blue
    '#005D5D', // dark teal
    '#9F1853', // magenta
    '#FA4D56', // coral
    '#198038', // green
    '#002D9C', // navy
    '#EE538B', // pink
    '#B28600', // mustard
    '#009D9A', // cyan
    '#012749', // dark navy
    '#8A3800', // brown
  ],
  // Option 5: Okabe-Ito — designed specifically for color-vision deficiency.
  // Pros: gold standard for accessibility; used in Nature/Science publications.
  // Cons: only 8 colors, so faster cycle collisions for partners with many FCs.
  okabeIto: [
    '#E69F00', // orange
    '#56B4E9', // sky blue
    '#009E73', // bluish green
    '#F0E442', // yellow
    '#0072B2', // blue
    '#D55E00', // vermillion
    '#CC79A7', // reddish purple
    '#000000', // black
  ],
};

export const colorMap = palettes.ibmCarbon;

export const markerForIndex = (index: number) => {
  const color = colorMap[index % colorMap.length];
  const markerSVG = `<svg stroke="black" fill="currentColor" stroke-width="10" viewBox="0 0 384 512" color="${color}" height="2em" width="2em" xmlns="http://www.w3.org/2000/svg"><path d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z"></path></svg>`;
  const iconUrl = encodeURI('data:image/svg+xml,' + markerSVG).replace('#', '%23');
  return iconUrl;
};

export const addressHasMetadata = (address?: Address) => {
  const meta = address?.validatedAddressMetadata;
  return meta?.latitude !== undefined && meta?.longitude !== undefined;
};

export const addresssNonContinentalUS = (address?: Address) =>
  !!address?.country && (address.country !== 'US' || ['AK', 'HI'].includes(address.country));

const getCoordinateMinMax = (
  markers: SimpleMapMarker[],
  minOrMax: 'max' | 'min',
  latOrLng: 'latitude' | 'longitude',
) => {
  if (minOrMax === 'max') {
    return markers.reduce((prev, val) => {
      const v = val[latOrLng];
      return v === undefined || prev > v ? prev : v;
    }, MIN_POSSIBLE_LAT_OR_LNG_VALUE);
  } else {
    return markers.reduce((prev, val) => {
      const v = val[latOrLng];
      return v === undefined || prev < v ? prev : v;
    }, MAX_POSSIBLE_LAT_OR_LNG_VALUE);
  }
};

export const getBounds = (markers: SimpleMapMarker[]) => {
  const maxLat = getCoordinateMinMax(markers, 'max', 'latitude');
  const minLat = getCoordinateMinMax(markers, 'min', 'latitude');
  const maxLng = getCoordinateMinMax(markers, 'max', 'longitude');
  const minLng = getCoordinateMinMax(markers, 'min', 'longitude');
  const southWest: [number, number] = [minLng, minLat];
  const northEast: [number, number] = [maxLng, maxLat];
  return [southWest, northEast] as [[number, number], [number, number]];
};
